// src/components/BookingCalendar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import 'primeicons/primeicons.css';
import './BookingCalendar.css';
import useAuth from "hooks/useAuth";

const BookingCalendar = () => {
    const [bookings, setBookings] = useState([]);
    const [filteredBookings, setFilteredBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [dateRange, setDateRange] = useState(null);

    const toast = useRef(null);
    const auth = useAuth();

    const API_URL = process.env.REACT_APP_API_PATH;

    // ✅ FETCH BOOKINGS - Don't send date filters initially
    useEffect(() => {
        if (!auth?.accessToken) return;

        const source = axios.CancelToken.source();

        const fetchBookings = async () => {
            setLoading(true);
            try {
                // Remove date params to get all data
                const response = await axios.get(`${API_URL}pdashboard/calendarstaff`, {
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`
                    },
                     params: {
                        userId: auth.userId,  // Pass userId from auth context
                        // Optional: add default date range if needed
                        // startDate: '2024-01-01',
                        // endDate: '2026-12-31'
                    },
                    // Don't send startDate/endDate to get all bookings
                    cancelToken: source.token
                });

                console.log('Fetched bookings:', response.data); // Debug log

                setBookings(response.data || []);
                setFilteredBookings(response.data || []);
            } catch (error) {
                if (axios.isCancel(error)) return;

                console.error('API Error:', error);

                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.response?.data?.message || 'Failed to load bookings',
                    life: 3000
                });
            } finally {
                setLoading(false);
            }
        };

        fetchBookings();

        return () => {
            source.cancel("Component unmounted");
        };

    }, [auth?.accessToken]);

    // ✅ FILTER BY DATE (client-side filtering)
    const handleDateFilter = (e) => {
        const value = e.value;
        setDateRange(value);

        if (!value || value.length !== 2) {
            setFilteredBookings(bookings);
            return;
        }

        const [startDate, endDate] = value;
        
        // Reset time to start of day for accurate comparison
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        const filtered = bookings.filter(booking => {
            if (!booking.pickupDate) return false;
            const pickupDate = new Date(booking.pickupDate);
            return pickupDate >= start && pickupDate <= end;
        });

        setFilteredBookings(filtered);
        
        if (filtered.length === 0) {
            toast.current?.show({
                severity: 'info',
                summary: 'No Results',
                detail: 'No bookings found in selected date range',
                life: 3000
            });
        }
    };

    // ✅ TAG HELPERS
    const getStatusTag = (status) => {
        const severity = {
            'Pending': 'warning',
            'Confirmed': 'success',
            'In Progress': 'info',
            'Completed': 'secondary',
            'Cancelled': 'danger'
        };
        return <Tag value={status || 'Unknown'} severity={severity[status] || 'secondary'} />;
    };

    const getPaymentStatusTag = (paymentStatus) => {
        const severity = {
            'Paid': 'success',
            'Pending': 'warning',
            'Failed': 'danger'
        };
        return <Tag value={paymentStatus || 'Pending'} severity={severity[paymentStatus] || 'secondary'} />;
    };

    // ✅ FORMATTERS
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'INR'
        }).format(amount || 0);
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // ✅ IMPROVED TIMELINE VIEW with better styling
    const timelineView = () => {
        if (!filteredBookings || filteredBookings.length === 0) {
            return <p className="no-data-message">No bookings found</p>;
        }

        const bookingsByDate = {};

        filteredBookings.forEach(booking => {
            if (!booking.pickupDate && !booking.deliveryDate) return;
            
            const pickupDate = booking.pickupDate ? new Date(booking.pickupDate).toDateString() : null;
            const deliveryDate = booking.deliveryDate ? new Date(booking.deliveryDate).toDateString() : null;

            if (pickupDate) {
                if (!bookingsByDate[pickupDate]) {
                    bookingsByDate[pickupDate] = { pickup: [], delivery: [] };
                }
                bookingsByDate[pickupDate].pickup.push(booking);
            }

            if (deliveryDate && deliveryDate !== pickupDate) {
                if (!bookingsByDate[deliveryDate]) {
                    bookingsByDate[deliveryDate] = { pickup: [], delivery: [] };
                }
                bookingsByDate[deliveryDate].delivery.push(booking);
            } else if (deliveryDate && deliveryDate === pickupDate) {
                // Same day pickup and delivery
                if (bookingsByDate[deliveryDate]) {
                    bookingsByDate[deliveryDate].delivery.push(booking);
                }
            }
        });

        const sortedDates = Object.keys(bookingsByDate)
            .sort((a, b) => new Date(a) - new Date(b));

        return (
            <div className="timeline-view">
                {sortedDates.map(date => (
                    <div key={date} className="timeline-card">
                        <div className="timeline-date-header">
                            <h3>{new Date(date).toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}</h3>
                        </div>

                        <div className="timeline-sections">
                            <div className="timeline-section pickup-section">
                                <div className="section-header">
                                    <i className="pi pi-arrow-up"></i>
                                    <strong>Pickups ({bookingsByDate[date].pickup.length})</strong>
                                </div>
                                {bookingsByDate[date].pickup.length === 0 ? (
                                    <p className="empty-message">No pickups scheduled</p>
                                ) : (
                                    <div className="booking-list">
                                        {bookingsByDate[date].pickup.map(booking => (
                                            <div 
                                                key={`pickup-${booking.id}`} 
                                                className="booking-item"
                                                onClick={() => {
                                                    setSelectedBooking(booking);
                                                    setDialogVisible(true);
                                                }}
                                            >
                                                <div className="booking-ref">{booking.bookingReference}</div>
                                                <div className="booking-status">{getStatusTag(booking.status)}</div>
                                                <div className="booking-time">
                                                    {booking.pickupDate && new Date(booking.pickupDate).toLocaleTimeString()}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="timeline-section delivery-section">
                                <div className="section-header">
                                    <i className="pi pi-arrow-down"></i>
                                    <strong>Deliveries ({bookingsByDate[date].delivery.length})</strong>
                                </div>
                                {bookingsByDate[date].delivery.length === 0 ? (
                                    <p className="empty-message">No deliveries scheduled</p>
                                ) : (
                                    <div className="booking-list">
                                        {bookingsByDate[date].delivery.map(booking => (
                                            <div 
                                                key={`delivery-${booking.id}`} 
                                                className="booking-item"
                                                onClick={() => {
                                                    setSelectedBooking(booking);
                                                    setDialogVisible(true);
                                                }}
                                            >
                                                <div className="booking-ref">{booking.bookingReference}</div>
                                                <div className="booking-status">{getStatusTag(booking.status)}</div>
                                                <div className="booking-time">
                                                    {booking.deliveryDate && new Date(booking.deliveryDate).toLocaleTimeString()}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // ✅ LOADING
    if (loading) {
        return (
            <div className="loading-container">
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="booking-calendar-container">
            <Toast ref={toast} />

            <div className="header">
                <div className="header-title">
                    <h2>Booking Timeline</h2>
                    <span className="booking-count">Total: {filteredBookings.length} bookings</span>
                </div>

                <div className="filter-controls">
                    <Calendar
                        value={dateRange}
                        onChange={handleDateFilter}
                        selectionMode="range"
                        readOnlyInput
                        showIcon
                        placeholder="Filter by date range"
                        className="date-filter"
                    />

                    {dateRange && (
                        <Button
                            icon="pi pi-times"
                            onClick={() => {
                                setDateRange(null);
                                setFilteredBookings(bookings);
                            }}
                            className="p-button-outlined"
                            tooltip="Clear filter"
                        />
                    )}
                </div>
            </div>

            {filteredBookings.length === 0 && !loading ? (
                <div className="no-bookings">
                    <i className="pi pi-calendar" style={{ fontSize: '3rem', opacity: 0.5 }}></i>
                    <p>No bookings found</p>
                    {dateRange && (
                        <Button 
                            label="Clear Filters" 
                            icon="pi pi-filter-slash"
                            onClick={() => {
                                setDateRange(null);
                                setFilteredBookings(bookings);
                            }}
                            className="p-button-text"
                        />
                    )}
                </div>
            ) : (
                timelineView()
            )}

            <Dialog
                header="Booking Details"
                visible={dialogVisible}
                style={{ width: '90vw', maxWidth: '500px' }}
                onHide={() => setDialogVisible(false)}
                className="booking-dialog"
            >
                {selectedBooking && (
                    <div className="booking-details">
                        <div className="detail-row">
                            <label>Reference:</label>
                            <span>{selectedBooking.bookingReference}</span>
                        </div>
                        <div className="detail-row">
                            <label>Status:</label>
                            {getStatusTag(selectedBooking.status)}
                        </div>
                        <div className="detail-row">
                            <label>Payment:</label>
                            {getPaymentStatusTag(selectedBooking.paymentStatus)}
                        </div>
                        <div className="detail-row">
                            <label>Pickup:</label>
                            <span>{formatDate(selectedBooking.pickupDate)}</span>
                        </div>
                        <div className="detail-row">
                            <label>Delivery:</label>
                            <span>{formatDate(selectedBooking.deliveryDate)}</span>
                        </div>
                        <div className="detail-row">
                            <label>Amount:</label>
                            <span className="amount">{formatCurrency(selectedBooking.totalAmount)}</span>
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
        
    );
};

export default BookingCalendar;